import {
    IModelingSessionService,
    IModelingSessionServiceCalculateQualitySubTabOptions,
    IModelingSessionServiceCalculateReferenceValuesOptions,
    IModelingSessionServiceCalculateSessionOptions,
    IModelingSessionServiceDatasetLeafResponse,
    IModelingSessionServiceExportDatasetResponse,
    IModelingSessionServiceOutlierResponse,
    IModelingSessionServiceReferenceValuesResponse,
    IModelingSessionServiceRequest,
    IModelingSessionServiceResponse,
    IModelingSessionServiceSummarizeTrialFitMetricsResponse,
    IModelingSessionServiceSummarizeTrialMetricsOptions,
} from "./ModelingSessionServices.IF";
import { IMetricParameterSet } from "../PA.Metric.Services/PA.Metrics.IF";
import { PAWebSocketClient } from "../PA.Core/PAWebSocketClient";

export class ModelingSessionService implements IModelingSessionService {
    private static wsc: PAWebSocketClient;

    constructor() {
        ModelingSessionService.wsc = new PAWebSocketClient();
    }

    CalculateReferenceValues(
        wbModelingSessionId: number,
        opts?: IModelingSessionServiceCalculateReferenceValuesOptions,
    ): Promise<IModelingSessionServiceReferenceValuesResponse> {
        let aCalculateRequest: IModelingSessionServiceRequest = {
            RequestType: "CalculateReferenceValues",
            RequestOptions: opts || {},
            wbModelingSessionId: wbModelingSessionId,
            GUID: "",
            bAllowLongRunning: true,
        };

        return ModelingSessionService.makeRequest(aCalculateRequest);
    }


    CalculateSession(
        wbModelingSessionId: number,
        opts?: IModelingSessionServiceCalculateSessionOptions,
    ): Promise<IModelingSessionServiceResponse> {
        let aCalculateRequest: IModelingSessionServiceRequest = {
            RequestType: "CalculateSession",
            RequestOptions: opts || {},
            wbModelingSessionId: wbModelingSessionId,
            GUID: "",
            bAllowLongRunning: true,
        };

        return ModelingSessionService.makeRequest(aCalculateRequest);
    }

    GetSessionStatus(wbModelingSessionId: number, opts?: any): Promise<IModelingSessionServiceResponse> {
        let aRequest: IModelingSessionServiceRequest = {
            RequestType: "GetStatus",
            RequestOptions: opts || {},
            wbModelingSessionId: wbModelingSessionId,
            GUID: "",
            bAllowLongRunning: false,
        };

        return ModelingSessionService.makeRequest(aRequest);
    }

    StopSession(wbModelingSessionId: number, opts?: any): Promise<IModelingSessionServiceResponse> {
        let aRequest: IModelingSessionServiceRequest = {
            RequestType: "StopSession",
            RequestOptions: opts || {},
            wbModelingSessionId: wbModelingSessionId,
            GUID: "",
            bAllowLongRunning: false,
        };

        return ModelingSessionService.makeRequest(aRequest);
    }

    SummarizeTrialFitMetrics(
        wbModelingSessionTrialId: number,
        metricParameterSet: IMetricParameterSet,
        options?: IModelingSessionServiceSummarizeTrialMetricsOptions,
    ): Promise<IModelingSessionServiceSummarizeTrialFitMetricsResponse> {
        let aSummarizeRequest: IModelingSessionServiceRequest = {
            RequestType: "SummarizeTrialFitMetrics",
            RequestOptions: options || {},
            wbModelingSessionTrialId: wbModelingSessionTrialId,
            metricParameterSet: metricParameterSet,
            GUID: "",
            bAllowLongRunning: false,
        };

        return ModelingSessionService.makeRequest(aSummarizeRequest);
    }

    ExportSessionDataset(
        wbModelingSessionId: number,
        opts?: any,
    ): Promise<IModelingSessionServiceExportDatasetResponse> {
        let aSummarizeRequest: IModelingSessionServiceRequest = {
            RequestType: "ExportDataSet",
            RequestOptions: opts || {},
            wbModelingSessionId: wbModelingSessionId,
            GUID: "",
            bAllowLongRunning: false,
        };

        aSummarizeRequest.RequestOptions.FileType = "Excel";

        return ModelingSessionService.makeRequest(aSummarizeRequest);
    }

    ExportTrialDatasetAndPredictions(
        wbModelingSessionId: number,
        opts?: any,
    ): Promise<IModelingSessionServiceExportDatasetResponse> {
        let aSummarizeRequest: IModelingSessionServiceRequest = {
            RequestType: "ExportDataSetAndPredictions",
            RequestOptions: opts || {},
            wbModelingSessionId: wbModelingSessionId,
            GUID: "",
            bAllowLongRunning: false,
        };

        aSummarizeRequest.RequestOptions.FileType = "Excel";

        return ModelingSessionService.makeRequest(aSummarizeRequest);
    }

    CalculateOutliers(
        wbModelingSessionTrialId: number,
        opts?: IModelingSessionServiceCalculateQualitySubTabOptions,
    ): Promise<IModelingSessionServiceOutlierResponse> {
        opts = opts || {};
        opts.SubTabName = "Outliers";

        return this.CalculateQualitySubTabs(wbModelingSessionTrialId, [opts]);
    }

    CalculateSkew(
        wbModelingSessionTrialId: number,
        opts?: IModelingSessionServiceCalculateQualitySubTabOptions,
    ): Promise<IModelingSessionServiceOutlierResponse> {
        opts = opts || {};
        opts.SubTabName = "Skew";

        return this.CalculateQualitySubTabs(wbModelingSessionTrialId, [opts]);
    }

    CalculateSignificance(
        wbModelingSessionTrialId: number,
        opts?: IModelingSessionServiceCalculateQualitySubTabOptions,
    ): Promise<IModelingSessionServiceOutlierResponse> {
        opts = opts || {};
        opts.SubTabName = "Significance";

        return this.CalculateQualitySubTabs(wbModelingSessionTrialId, [opts]);
    }

    CalculateBias(
        wbModelingSessionTrialId: number,
        opts?: IModelingSessionServiceCalculateQualitySubTabOptions,
    ): Promise<IModelingSessionServiceOutlierResponse> {
        opts = opts || {};
        opts.SubTabName = "Bias";

        return this.CalculateQualitySubTabs(wbModelingSessionTrialId, [opts]);
    }

    CalculateCollinearity(
        wbModelingSessionTrialId: number,
        opts?: IModelingSessionServiceCalculateQualitySubTabOptions,
    ): Promise<IModelingSessionServiceOutlierResponse> {
        opts = opts || {};
        opts.SubTabName = "Collinearity";

        return this.CalculateQualitySubTabs(wbModelingSessionTrialId, [opts]);
    }

    CalculateQualitySubTabs(
        wbModelingSessionTrialId: number,
        tabOptions: IModelingSessionServiceCalculateQualitySubTabOptions[],
        otherOpts?: any,
    ): Promise<IModelingSessionServiceOutlierResponse> {
        otherOpts = otherOpts || {};

        otherOpts.SubTabs = JSON.stringify(tabOptions);
        let aSummarizeRequest: IModelingSessionServiceRequest = {
            RequestType: "CalculateQualitySubTabs",
            RequestOptions: otherOpts,
            wbModelingSessionTrialId: wbModelingSessionTrialId,
            GUID: "",
            bAllowLongRunning: false,
        };

        return ModelingSessionService.makeRequest(aSummarizeRequest);
    }

    CalculateElasticity(
        wbModelingSessionId: number,
        opts?: IModelingSessionServiceCalculateSessionOptions,
    ): Promise<IModelingSessionServiceResponse> {
        let aCalculateElasticityRequest: IModelingSessionServiceRequest = {
            RequestType: "CalculateElasticity",
            RequestOptions: opts || {},
            wbModelingSessionId: wbModelingSessionId,
            GUID: "",
            bAllowLongRunning: false,
        };

        return ModelingSessionService.makeRequest(aCalculateElasticityRequest);
    }

    private static async makeRequest(aRequest: IModelingSessionServiceRequest): Promise<any> {
        let results: any;

        try {
            results = await ModelingSessionService.wsc.invoke("ModelingSessionService", aRequest, {
                ackTimeout: 60 * 1000 * 5,
            });
        } catch (err) {
            results = {
                bSuccess: false,
                sErrors: [err.message],
                sWarnings: [],
                GUID: "",
                results: undefined,
            };
        }

        return results;
    }

    GetDatasetLeafItems(wbModelingSessionId: number, opts?: any): Promise<IModelingSessionServiceDatasetLeafResponse> {
        return Promise.resolve(undefined);
    }
}
